import { useEffect, useState } from "react";
import { Phone, X } from "lucide-react";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

// Mobile-only "pocket" popup. Slim floating card at the top that shows
// Kit's phone number BIG for one-tap calling — WITHOUT dimming/covering
// any content behind it. Auto-hides after 12s if not tapped so it never
// blocks images or the hero for long. Dismissible; dismissal remembered
// for 24h.
export default function MobilePocketPopup({
  phone = "+12148155966",
  displayPhone = "(214) 815-5966",
}) {
  const [open, setOpen] = useState(false);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.innerWidth >= 768) return undefined; // mobile only

    // 24h dismissal cache
    try {
      const raw = localStorage.getItem("plugitai_pocket_dismissed_v1");
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < 24 * 60 * 60 * 1000) {
          return undefined;
        }
      }
    } catch { /* ignore */ }

    const openT = setTimeout(() => {
      setOpen(true);
      requestAnimationFrame(() => setEntering(true));
    }, 900);

    // Auto-collapse after 8s so it never lingers over hero images.
    const closeT = setTimeout(() => {
      setEntering(false);
      setTimeout(() => setOpen(false), 260);
    }, 8000);

    // Dismiss the moment the user starts scrolling — they've seen it,
    // and we don't want a fixed banner blocking any images below the fold.
    let scrollTriggered = false;
    const onScroll = () => {
      if (scrollTriggered) return;
      if (window.scrollY > 40) {
        scrollTriggered = true;
        setEntering(false);
        setTimeout(() => setOpen(false), 240);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(openT);
      clearTimeout(closeT);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = (e) => {
    e?.stopPropagation();
    setEntering(false);
    setTimeout(() => setOpen(false), 240);
    try {
      localStorage.setItem("plugitai_pocket_dismissed_v1", String(Date.now()));
    } catch { /* ignore */ }
  };

  const callHref = `tel:${phone}`;
  const smsBody = "Hi Kit — saw your site, got a quick question.";
  const smsHref = `sms:${phone}?&body=${encodeURIComponent(smsBody)}`;

  if (!open) return null;

  return (
    <div
      className="md:hidden fixed left-2 right-2 top-[72px] z-[45] pointer-events-none"
      style={{
        transform: entering ? "translateY(0)" : "translateY(-24px)",
        opacity: entering ? 1 : 0,
        transition:
          "transform 300ms cubic-bezier(0.2,0.7,0.2,1), opacity 220ms ease",
      }}
      data-testid="pocket-popup"
    >
      <div
        className="relative rounded-2xl overflow-hidden border shadow-2xl pointer-events-auto"
        style={{
          background:
            "linear-gradient(135deg, rgba(11,21,36,0.94) 0%, rgba(16,27,46,0.94) 100%)",
          borderColor: `${teal}66`,
          backdropFilter: "blur(10px)",
          boxShadow: `0 22px 60px -18px ${teal}55, 0 12px 40px -12px rgba(0,0,0,0.6)`,
        }}
      >
        {/* Dismiss */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 z-10 w-7 h-7 rounded-full flex items-center justify-center"
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.7)",
          }}
          data-testid="pocket-popup-close"
        >
          <X size={13} />
        </button>

        {/* Compact tap-to-call row */}
        <a
          href={callHref}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-3 px-3 py-2.5 pr-9"
          data-testid="pocket-popup-call"
        >
          <div
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
              boxShadow: `0 8px 20px -8px ${teal}aa`,
            }}
          >
            <Phone size={16} color="white" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-[9px] font-black uppercase tracking-[0.24em] leading-none"
              style={{ color: teal }}
            >
              Tap to call Kit
            </div>
            <div
              className="mt-0.5 font-black text-white leading-none whitespace-nowrap"
              style={{
                fontSize: "clamp(1.4rem, 6.5vw, 1.75rem)",
                letterSpacing: "-0.01em",
              }}
              data-testid="pocket-popup-number"
            >
              {displayPhone}
            </div>
          </div>
        </a>

        {/* SMS row — tiny, non-blocking */}
        <a
          href={smsHref}
          onClick={(e) => e.stopPropagation()}
          className="block text-center py-1.5 border-t text-[10px] font-black uppercase tracking-[0.22em]"
          style={{
            borderColor: "rgba(255,255,255,0.06)",
            color: gold,
            background: "rgba(255,215,0,0.04)",
          }}
          data-testid="pocket-popup-sms"
        >
          or text instead →
        </a>
      </div>
    </div>
  );
}
